<script setup>
import { ref, computed, onUnmounted, watch, onMounted } from 'vue';
import Map from 'dashboard/components/ui/Map.vue';
import MapMarker from 'dashboard/components/ui/MapMarker.vue';
import MapPopup from 'dashboard/components/ui/MapPopup.vue';
import MapControls from 'dashboard/components/ui/MapControls.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const title = computed(() => props.widget.title);
const mapConfig = computed(() => ({
  center: props.widget.properties?.center || [2.3522, 48.8566],
  zoom: props.widget.properties?.zoom || 12,
}));

const pins = computed(() => {
  const pinsList = props.widget.properties?.pins || [];
  // eslint-disable-next-line no-console
  console.log('[MapWidget] Pins computed:', pinsList);
  return pinsList;
});
const legendStyles = computed(
  () => props.widget.properties?.legendStyles || []
);

const lastClickedCoords = ref(null);
let timer = null;

const handleMapClick = event => {
  const { lng, lat } = event.lngLat;
  const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  lastClickedCoords.value = coords;
  navigator.clipboard.writeText(coords);
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    lastClickedCoords.value = null;
  }, 3000);
};

const getPinStyle = pin => {
  const style = legendStyles.value.find(s => s.status === pin.status);
  return style || { color: '#0F172A', label: pin.status };
};

const legendItems = computed(() => {
  const activeStatuses = [...new Set(pins.value.map(p => p.status))];
  return legendStyles.value.filter(s => activeStatuses.includes(s.status));
});

onMounted(() => {
  // eslint-disable-next-line no-console
  console.log('[MapWidget] Mounted with pins:', pins.value);
});

watch(
  pins,
  newPins => {
    // eslint-disable-next-line no-console
    console.log('[MapWidget] Pins changed:', newPins);
  },
  { immediate: true, deep: true }
);

onUnmounted(() => {
  if (timer) clearTimeout(timer);
});
</script>

<template>
  <div class="h-full flex flex-col relative font-inter group">
    <div v-if="title" class="mb-6 flex items-center justify-between px-1">
      <div class="flex items-center gap-3">
        <div
          class="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 border border-slate-100 text-slate-400 group-hover:text-slate-900 group-hover:bg-white transition-all duration-200"
        >
          <span class="i-lucide-map-pin h-3.5 w-3.5" />
        </div>
        <h3 class="text-[13px] font-bold text-slate-900 tracking-tight">
          {{ title }}
        </h3>
      </div>

      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="lastClickedCoords"
          class="flex items-center gap-2 px-2.5 py-1 bg-slate-950 text-white rounded-md text-[10px] font-bold shadow-xl"
        >
          <span class="i-lucide-check size-3 text-emerald-400" />
          <span>COPIED</span>
        </div>
      </Transition>
    </div>

    <div
      class="flex-1 min-h-[400px] w-full rounded-2xl overflow-hidden relative border border-slate-200/60 shadow-lg group/map"
    >
      <Map
        :center="mapConfig.center"
        :zoom="mapConfig.zoom"
        theme="light"
        class="h-full w-full"
        @click="handleMapClick"
      >
        <MapControls show-locate show-fullscreen />
        <MapMarker
          v-for="(pin, index) in pins"
          :key="pin.id || `pin-${index}-${pin.lat}-${pin.lng}`"
          :longitude="pin.lng"
          :latitude="pin.lat"
          :color="getPinStyle(pin).color"
        >
          <!-- Custom Marker Design (shadcn-like) -->
          <div
            class="relative flex items-center justify-center cursor-pointer group/marker"
          >
            <!-- Pulsing outer ring -->
            <div
              class="absolute w-8 h-8 rounded-full animate-ping opacity-20"
              :style="{ backgroundColor: getPinStyle(pin).color }"
            />
            <!-- Inner solid marker -->
            <div
              class="relative w-4 h-4 rounded-full border-2 border-white shadow-lg transition-transform duration-300 group-hover/marker:scale-125"
              :style="{ backgroundColor: getPinStyle(pin).color }"
            />
          </div>

          <!-- Popup content -->
          <MapPopup v-if="pin.title || pin.description">
            <div class="min-w-[220px] p-1">
              <div v-if="pin.title" class="flex items-center gap-2.5 mb-2">
                <div
                  class="w-2 h-2 rounded-full shadow-sm animate-pulse"
                  :style="{ backgroundColor: getPinStyle(pin).color }"
                />
                <span
                  class="text-[14px] font-black text-slate-900 tracking-tight leading-none"
                  >{{ pin.title }}</span>
              </div>
              <p
                v-if="pin.description"
                class="text-[12px] font-medium text-slate-500 leading-relaxed border-l-2 border-slate-100 pl-3 ml-1 py-0.5"
              >
                {{ pin.description }}
              </p>
              <div class="mt-3 pt-2 border-t border-slate-50 flex justify-end">
                <span
                  class="text-[9px] font-black text-slate-300 uppercase tracking-widest"
                  >Location Verified</span>
              </div>
            </div>
          </MapPopup>
        </MapMarker>

        <!-- Legend (shadcn-like) -->
        <div
          v-if="legendItems.length > 0"
          class="absolute bottom-6 left-6 bg-white/80 backdrop-blur-xl rounded-2xl p-4 z-10 border border-slate-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col gap-3 min-w-[140px]"
        >
          <span
            class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1"
            >Legend</span>
          <div
            v-for="item in legendItems"
            :key="item.status"
            class="flex items-center gap-3 group/legend cursor-pointer"
          >
            <div
              class="w-2.5 h-2.5 rounded-full shadow-sm transition-transform group-hover/legend:scale-125"
              :style="{ backgroundColor: item.color }"
            />
            <span
              class="text-[11px] text-slate-600 font-bold uppercase tracking-wider group-hover/legend:text-slate-900 transition-colors"
              >{{ item.label }}</span>
          </div>
        </div>
      </Map>
    </div>
  </div>
</template>
